import classNames from 'classnames';
import { FC, Fragment, PropsWithChildren, ReactElement } from 'react';

import { Page, Text, Flex, Button, useBreakpoint } from '@servicetitan/anvil2';
import { CenteredSpinner } from '@servicetitan/marketing-ui';

import { CenteredError } from '../../../common/components/centered-error/centered-error';
import { TenantSidebarNav } from './tenant-sidebar-nav';
import { Footer } from './footer';
import { PanelDialogProvider, usePanelDialog } from './panel-dialog-context';
import { BREAKPOINTS_SMALLER_LG } from '../../utils/ux';

import * as Styles from './manage-tenant-layout.module.less';

interface ManageLayoutProps extends PropsWithChildren {
    panelContent?: ReactElement;
    loading?: boolean;
    error?: boolean;
    breadCrumb?: ReactElement;
    headline?: string;
    title?: ReactElement;
}

const ManageTenantLayoutContent: FC<ManageLayoutProps> = ({
    panelContent,
    children,
    breadCrumb,
    headline,
    loading,
    error,
}) => {
    const breakpoint = useBreakpoint();
    const isDialogMode = BREAKPOINTS_SMALLER_LG.includes(breakpoint?.name ?? '');
    const { panelDialogState, setPanelDialogState } = usePanelDialog();
    const showPanel = !!panelContent;

    return (
        <Flex direction="column" grow={1} className={classNames({ skeleton: loading })}>
            <HeaderSection breadCrumb={breadCrumb} headline={headline} />
            <Page data-anv={undefined} className={classNames('border-top-i', Styles.page)}>
                <Page.Sidebar>
                    <TenantSidebarNav />
                </Page.Sidebar>

                <Page.Content className={Styles.pageContent}>
                    {loading ? (
                        <CenteredSpinner />
                    ) : error ? (
                        <CenteredError />
                    ) : (
                        <div className="p-b-3">{children}</div>
                    )}
                </Page.Content>

                {showPanel && (
                    <Page.Panel
                        open={isDialogMode ? panelDialogState : undefined}
                        className={Styles.pagePanel}
                    >
                        {isDialogMode ? (
                            <Fragment>
                                <div className={Styles.dialogContentScrollable}>{panelContent}</div>
                                {panelDialogState && (
                                    <div className={Styles.dialogButtonContainer}>
                                        <Button onClick={() => setPanelDialogState(false)}>
                                            Close
                                        </Button>
                                    </div>
                                )}
                            </Fragment>
                        ) : (
                            <div>{panelContent}</div>
                        )}
                    </Page.Panel>
                )}
                <Footer />
            </Page>
        </Flex>
    );
};

export const ManageTenantLayout: FC<ManageLayoutProps> = props => {
    return (
        <PanelDialogProvider hasPanel={!!props.panelContent}>
            <ManageTenantLayoutContent {...props} />
        </PanelDialogProvider>
    );
};

const HeaderSection: FC<{ breadCrumb?: ReactElement; headline?: string }> = ({
    breadCrumb,
    headline = '-',
}) => {
    return (
        <Flex direction="column" gap={4} className="p-x-5-i p-y-4-i">
            {breadCrumb}
            <Text
                variant="headline"
                el="h2"
                size="large"
                className={classNames('m-b-0-i', 'skeleton-text-33')}
            >
                {headline}
            </Text>
        </Flex>
    );
};
